import React, { useEffect, useState } from "react"
import { Alert, Button, Form, message, Row, Spin, Typography } from "antd"
import { RollbackOutlined } from "@ant-design/icons"
import { useHistory, useParams } from "react-router-dom"
import { useCompany } from "../../../contexts/CompanyContext"
import { useConfig } from "../../../contexts/ConfigContext"
import { useSession } from "../../../contexts/CashSessionContext"
import { useUser } from "../../../contexts/UserContext"
import { useRegister } from "../../../contexts/RegisterContext"
import { useSecureRequest } from "../../../hooks/useSecureRequest"
import { IMovementRS, TMovementElementItem } from "../../../models/salesModel"
import { 
  createId, 
  textNumberFormater, 
  isFullReturnOrAnnulment, 
  isPhysicalReturn 
} from "../../../utils"
import {
  calculateNoteTotals,
  computeItemPricing,
  getFinancialImpactMessage,
  resolveNoteItemId,
  NoteBillItemsCatalog,
  NoteConfigCard,
  NoteHeader,
  NoteItemsDetailCard,
  NoteSummaryFooter
} from "./create-note"

const { Title } = Typography

function ViewCreateNote() {
  const { uid } = useParams<{ uid: string }>()
  const { config } = useConfig()
  const { company } = useCompany()
  const { user } = useUser()
  const { securePost } = useSecureRequest()
  const { session, updateSession } = useSession()
  const { register } = useRegister()
  const history = useHistory()
  const [form] = Form.useForm()

  const usesCashSessions = company.salePlan?.key !== 'BASIC'
  const hasActiveSession = Boolean(session.currentSelected?.id)

  useEffect(() => {
    if (usesCashSessions && user?.document_number && company.current?.uid && !session.currentSelected?.id) {
      securePost('/sale/cash-session/get-current-session', {
        company: company.current.uid,
        idUser: user.document_number
      }).then(res => {
        if (res?.status === 200 && res?.cashSession) {
          updateSession({ currentSelected: res.cashSession })
        }
      }).catch(err => {
        console.warn("Could not check active cash session:", err)
      })
    }
  }, [usesCashSessions, user?.document_number, company.current?.uid, session.currentSelected?.id])

  const [bill, setBill] = useState<IMovementRS | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [noteItems, setNoteItems] = useState<TMovementElementItem[]>([])
  const [noteType, setNoteType] = useState<'CREDIT_NOTE' | 'DEBIT_NOTE'>('CREDIT_NOTE')
  const [reasonOptions, setReasonOptions] = useState<any[]>([])

  const [isAnnulment, setIsAnnulment] = useState(false)
  const [isPhysical, setIsPhysical] = useState(true)
  const [itemRemainingQty, setItemRemainingQty] = useState<Record<number, number>>({})
  const [hasAnnulment, setHasAnnulment] = useState(false)
  const [isItemsCatalogCollapsed, setIsItemsCatalogCollapsed] = useState(false)

  const computePricing = (item: TMovementElementItem, quantity: number, customUnitPrice?: number) => {
    return computeItemPricing(item, quantity, register.taxesOptions, customUnitPrice)
  }

  useEffect(() => {
    if (noteType === 'CREDIT_NOTE') {
      setReasonOptions(register.creditNoteOptions)
    } else {
      setReasonOptions(register.debitNoteOptions)
    }
    form.setFieldsValue({ reason: undefined })
    setIsAnnulment(false)
    setIsPhysical(true)
    setNoteItems([])
  }, [noteType, register.creditNoteOptions, register.debitNoteOptions, form])

  useEffect(() => {
    const fetchBillAndNotes = async () => {
      setLoading(true)
      try {
        const response = await securePost('/inventory/movement/get-movement', {
          company: company.current.uid,
          uidMovement: uid,
          module: config.module
        })
        if (response.status === 200) {
          const fetchedBill: IMovementRS = response.movement
          setBill(fetchedBill)
          setNoteItems([])
          form.setFieldsValue({
            destination: fetchedBill.payment_term === 'CREDIT' ? 'CREDIT' : 'CASH'
          })

          // Fetch related notes (summaries from Algolia)
          const notesResponse = await securePost('/inventory/movement/get-notes-by-reference', {
            company: company.current.uid,
            referenceId: fetchedBill.document_number,
            altReferenceId: fetchedBill.electronic_invoice_number,
            module: config.module
          })

          let fullNotes: IMovementRS[] = []
          if (notesResponse.status === 200 && notesResponse.noteList?.length > 0) {
            const noteSummaries: IMovementRS[] = notesResponse.noteList
             
            // Fetch full details for each note from Firestore
            const detailPromises = noteSummaries.map(note => 
              securePost('/inventory/movement/get-movement', {
                company: company.current.uid,
                uidMovement: note.uid,
                module: config.module
              })
            )
             
            const detailResponses = await Promise.all(detailPromises)
            fullNotes = detailResponses
              .filter(res => res.status === 200 && res.movement)
              .map(res => res.movement)
          }

          // Calculate remaining quantities
          let annulmentExists = false
          const productReturnedQty: Record<number, number> = {}
          
          fullNotes.forEach(note => {
             if (isFullReturnOrAnnulment(note.correction_note_reason_key, note.correction_note_reason_code, note.correction_note_reason_description)) annulmentExists = true
             if (['APPROVED', 'COMPLETED'].includes(note.status || '') && note.document_type === 'CREDIT_NOTE') {
               const isReturn = isPhysicalReturn(note.correction_note_reason_key, note.correction_note_reason_code, note.correction_note_reason_description)
               if (isReturn) {
                 note.item_list?.forEach(ni => {
                   if (ni.track_inventory === false || ni.is_custom_charge || ni.register_type === 'SERVICE') {
                     return
                   }
                   const pid = ni.element_id
                   if (pid) {
                     const qty = Number(ni.quantity) || 0
                     productReturnedQty[pid] = (productReturnedQty[pid] || 0) + qty
                   }
                 })
               }
             }
          })
          
          setHasAnnulment(annulmentExists)

          // Distribute returned quantities across original rows
          const finalRemaining: Record<number, number> = {}
          fetchedBill.item_list?.forEach(row => {
            const pid = row.element_id
            if (pid) {
              const returned = productReturnedQty[pid] || 0
              const original = Number(row.quantity) || 0
              const toSubtract = Math.min(original, returned)
              finalRemaining[row.id] = original - toSubtract
              productReturnedQty[pid] = returned - toSubtract
            } else {
              finalRemaining[row.id] = Number(row.quantity) || 0
            }
          })
          
          setItemRemainingQty(finalRemaining)

        } else {
          message.error("No se pudo cargar la factura original")
        }
      } catch (error) {
        console.error("Error fetching bill:", error)
        message.error("No se pudo cargar la factura original")
      } finally {
        setLoading(false)
      }
    }

    if (uid && company.current.uid) {
      void fetchBillAndNotes()
    }
  }, [uid, company, config.module, securePost, form])

  const handleAddItem = (originalItem: TMovementElementItem) => {
    if (noteItems.some(item => item.id === originalItem.id)) {
      message.warning("El ítem ya ha sido añadido a la nota")
      return
    }
    const currentReason = reasonOptions.find(opt => opt.key === form.getFieldValue("reason"))
    const physical = isPhysicalReturn(currentReason?.key, currentReason?.legal_code, currentReason?.string_value)
    const remaining = itemRemainingQty[originalItem.id] || 0
    if (noteType === 'CREDIT_NOTE' && physical && remaining <= 0) {
      message.warning("No quedan unidades disponibles para devolver de este ítem")
      return
    }

    // Default to 1 or available limit
    const maxAvailable = physical ? remaining : (originalItem.quantity || 1)
    const qtyToAdd = Math.min(1, maxAvailable)
    
    const pricing = computePricing(originalItem, qtyToAdd)
    const newItem = { 
      ...originalItem, 
      ...pricing,
      track_inventory: physical ? (originalItem.track_inventory !== false) : false
    }
    setNoteItems([...noteItems, newItem as any])
  }

  const handleAddCustomCharge = () => {
    const selectedReason = reasonOptions.find(opt => opt.key === form.getFieldValue("reason"))
    const customId = -Math.floor(Date.now() % 1000000 + Math.random() * 1000)
    const chargeName = selectedReason?.string_value || (noteType === 'CREDIT_NOTE' ? "Descuento / Ajuste comercial" : "Cargo adicional")
    const customItem: any = {
      id: customId,
      element_id: null,
      name: chargeName,
      item_name: chargeName,
      unit_price: 0,
      quantity: 1,
      total_amount: 0,
      discount_rate: 0,
      discount_amount: 0,
      total_tax_amount: 0,
      total_amount_full: 0,
      total_amount_net: 0,
      taxes_list: [],
      track_inventory: false,
      register_type: "SERVICE",
      is_custom_charge: true
    }
    setNoteItems([...noteItems, customItem])
  }

  const removeItem = (id: number) => {
    setNoteItems(noteItems.filter(item => item.id !== id))
  }

  const updateItemQuantity = (id: number, quantity: number) => {
    const original = bill?.item_list.find(i => i.id === id)
    const remaining = itemRemainingQty[id] ?? original?.quantity ?? 0
    const maxAllowed = (noteType === 'CREDIT_NOTE' && isPhysical) ? remaining : (original?.quantity || remaining)
    
    if (noteType === 'CREDIT_NOTE' && isPhysical && quantity > maxAllowed) {
      message.error(`La cantidad excede lo disponible para devolver (${maxAllowed})`)
      return
    }
    setNoteItems(noteItems.map(item => {
      if (item.id === id) {
        const pricing = computePricing(item, quantity, item.unit_price)
        return { ...item, ...pricing }
      }
      return item
    }))
  }

  const updateItemUnitPrice = (id: number, unit_price: number) => {
    const original = bill?.item_list?.find(i => i.id === id)
    let finalPrice = unit_price
    if (noteType === 'CREDIT_NOTE' && original && !original.is_custom_charge && unit_price > (original.unit_price || 0)) {
      message.warning(`El valor a rebajar ($ ${textNumberFormater(unit_price)}) no puede superar el precio original ($ ${textNumberFormater(original.unit_price)})`)
      finalPrice = original.unit_price || 0
    }
    setNoteItems(noteItems.map(item => {
      if (item.id === id) {
        const pricing = computePricing(item, item.quantity || 1, finalPrice)
        return { ...item, ...pricing }
      }
      return item
    }))
  }

  const updateItemName = (id: number, name: string) => {
    setNoteItems(noteItems.map(item => {
      if (item.id === id) {
        return { ...item, name, item_name: name }
      }
      return item
    }))
  }

  const handleReasonChange = (value: string) => {
    const selectedReason = reasonOptions.find(opt => opt.key === value)
    const isTotal = isFullReturnOrAnnulment(value, selectedReason?.legal_code, selectedReason?.string_value)
    const physical = isPhysicalReturn(value, selectedReason?.legal_code, selectedReason?.string_value)

    setIsPhysical(physical)

    if (noteType === 'CREDIT_NOTE') {
      if (isTotal) {
        setIsAnnulment(true)
        if (bill?.item_list) {
          const allItems = bill.item_list
            .filter(item => (itemRemainingQty[item.id] || 0) > 0)
            .map(item => {
              const qty = itemRemainingQty[item.id] || 0
              const pricing = computePricing(item, qty)
              return { ...item, ...pricing, track_inventory: item.track_inventory !== false }
            })
          setNoteItems(allItems as any[])
          if (allItems.length === 0) {
            message.warning("No hay ítems con cantidades disponibles para devolver en esta factura")
          }
        }
      } else {
        setIsAnnulment(false)
        setNoteItems([])
      }
    } else {
      setIsAnnulment(false)
      if (selectedReason?.string_value) {
        setNoteItems(prev => prev.map(item => {
          const customItem = item as any
          if (customItem.is_custom_charge && (item.name === "Cargo adicional" || !item.name)) {
            return { ...item, name: selectedReason.string_value, item_name: selectedReason.string_value }
          }
          return item
        }))
      }
    }
  }

  const totals = calculateNoteTotals(noteItems)

  const onFinish = async (values: any) => {
    if (noteItems.length === 0) {
      message.error("Debe añadir al menos un ítem o concepto a la nota")
      return
    }

    const hasZeroAmount = noteItems.some(i => (i.total_amount_net || i.total_amount || 0) <= 0)
    if (hasZeroAmount) {
      message.error("Todos los ítems de la nota deben tener un valor mayor a cero")
      return
    }

    // Validación estricta para Notas Crédito financieras: el ajuste unitario no puede superar el precio facturado original
    if (noteType === 'CREDIT_NOTE' && !isPhysical) {
      const invalidPriceItem = noteItems.find(i => {
        if (i.is_custom_charge) return false
        const orig = bill?.item_list?.find(o => o.id === i.id)
        return orig && (Number(i.unit_price) || 0) > (Number(orig.unit_price) || 0)
      })
      if (invalidPriceItem) {
        const orig = bill?.item_list?.find(o => o.id === invalidPriceItem.id)
        message.error(`El ajuste en "${invalidPriceItem.name}" ($ ${textNumberFormater(invalidPriceItem.unit_price || 0)}) supera el precio facturado original ($ ${textNumberFormater(orig?.unit_price ?? 0)})`)
        return
      }
    }

    // Validación de cantidad en Notas Crédito
    if (noteType === 'CREDIT_NOTE') {
      const invalidQtyItem = noteItems.find(i => {
        if (i.is_custom_charge) return false
        const maxQty = isPhysical 
          ? (itemRemainingQty[i.id] ?? 0)
          : (bill?.item_list?.find(o => o.id === i.id)?.quantity ?? 0)
        return (Number(i.quantity) || 0) > maxQty || (Number(i.quantity) || 0) <= 0
      })
      if (invalidQtyItem) {
        message.error(`La cantidad en "${invalidQtyItem.name}" no es válida o supera el saldo permitido`)
        return
      }
    }

    setSubmitting(true)
    try {
      const selectedReason = reasonOptions.find(opt => opt.key === values.reason)
      const cashPay = register.paymentOptions.find(el => el.key === "CASH")
      const destination = values.destination || (bill?.payment_term === 'CREDIT' ? 'CREDIT' : 'CASH')
      const isCreditNote = noteType === 'CREDIT_NOTE'
      const isCash = destination === 'CASH'

      const payments = isCash ? [{
        payment_method: 'CASH',
        amount: totals.total_amount_net,
        payment_method_code: cashPay?.legal_code
      }] : []
      
      const isReturn = isCreditNote && isPhysicalReturn(selectedReason?.key, selectedReason?.legal_code, selectedReason?.string_value)
      let storehouseToId: number | undefined = undefined
      if (isReturn) {
        storehouseToId = bill?.storehouse_from_id
      }

      const noteData: Partial<IMovementRS> = {
        company_id: company.current.id,
        contact_id: bill?.contact_id,
        contact_name: bill?.contact_name,
        contact_document: bill?.contact_document,
        reference_document: (bill?.document_number || bill?.electronic_invoice_number) ?? undefined,
        reference_document_uid: bill?.uid,
        document_number: (isCreditNote ? 'NCR' : 'NDB') + createId(6),
        document_type: noteType,
        invoice_type: bill?.invoice_type === 'ELECTRONIC_INVOICE' ? 'ELECTRONIC_INVOICE' : noteType,
        status: 'PENDING',
        payment_term: destination,
        total_amount: totals.total_amount,
        total_tax_amount: totals.total_tax_amount,
        total_discount_amount: totals.total_discount_amount,
        total_amount_full: totals.total_amount_full,
        total_amount_net: totals.total_amount_net,
        taxes_list: totals.taxes_list,
        item_list: noteItems.map(el => {
          return {
            ...el,
            id: resolveNoteItemId(el),
            track_inventory: isReturn ? (el.track_inventory !== false) : false
          }
        }),
        items: noteItems.length,
        correction_note_reason_code: selectedReason?.legal_code,
        correction_note_reason_key: selectedReason?.key,
        correction_note_reason_description: selectedReason?.string_value,
        notes: values.notes,
        cash_session_id: isCash ? session.currentSelected?.id : undefined,
        cash_session_uid: isCash ? session.currentSelected?.uid : undefined,
        storehouse_to_id: storehouseToId,
        created_by_user_id: user.id,
        created_by_user_name: user.name,
        created_by_user_document: user.document_number,
        approved_by_user_id: user.id,
        approved_by_user_name: user.name,
        approved_by_user_document: user.document_number,
        pos_uid: bill?.pos_uid,
        pos_name: bill?.pos_name,
        payments,
        cash_return_amount: 0
      }

      const response = await securePost('/inventory/movement/create-movement', {
        company: company.current.uid,
        movement: { ...noteData, status: 'PENDING' },
        module: config.module
      })

      if (response.status === 200) {
        const updateResponse = await securePost('/inventory/movement/update-movement', {
          company: company.current.uid,
          uidMovement: response.movementUid,
          movement: { ...noteData, status: 'APPROVED', uid: response.movementUid },
          typeMovement: noteType
        })

        if (updateResponse.status === 200) {
          message.success(`${isCreditNote ? 'Nota de Crédito' : 'Nota de Débito'} creada con éxito`)
          history.push(`/bill-detail/${uid}`)
        } else {
          message.error(updateResponse?.message || "Error al registrar la nota")
        }
      } else {
        message.error(response?.message || "Error al crear la nota")
      }
    } catch (error) {
      console.error("Error creating note:", error)
      message.error("Ocurrió un error inesperado al procesar la nota")
    } finally {
      setSubmitting(false)
    }
  }

  const reason = Form.useWatch("reason", form)
  const destinationWatch = Form.useWatch("destination", form) || (bill?.payment_term === 'CREDIT' ? 'CREDIT' : 'CASH')
  const financialImpactMessage = getFinancialImpactMessage(destinationWatch, noteType, usesCashSessions, hasActiveSession)

  if (loading) return <Spin fullscreen tip="Cargando..." />

  if (hasAnnulment) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Title level={4}>Esta factura ya ha sido anulada</Title>
        <Alert
          message="No es posible crear más notas porque existe una nota de anulación para esta factura."
          type="warning"
          showIcon
          style={{ marginBottom: 24, display: 'inline-block' }}
        />
        <br />
        <Button icon={<RollbackOutlined />} onClick={() => history.goBack()}>Volver a Factura</Button>
      </div>
    )
  }

  return (
    <div style={{ padding: config.isMobile ? "12px 8px" : "16px 24px" }}>
      <Spin spinning={submitting} size="large" tip="Procesando transacción..." fullscreen />
      
      <NoteHeader
        documentNumber={bill?.document_number}
        isMobile={config.isMobile}
        onBack={() => history.goBack()}
      />

      <Form 
        form={form} 
        layout="vertical" 
        onFinish={onFinish} 
        initialValues={{ type: 'CREDIT_NOTE', destination: bill?.payment_term === 'CREDIT' ? 'CREDIT' : 'CASH' }}
      >
        <NoteConfigCard
          noteType={noteType}
          onNoteTypeChange={(val) => setNoteType(val)}
          reasonOptions={reasonOptions}
          onReasonChange={handleReasonChange}
          destinationWatch={destinationWatch}
          isAnnulment={isAnnulment}
          isPhysical={isPhysical}
          usesCashSessions={usesCashSessions}
          hasActiveSession={hasActiveSession}
          sessionId={session.currentSelected?.id}
          cashierName={session.currentSelected?.cashier_name || user.name}
        />

        <Row gutter={[16, 16]}>
          <NoteBillItemsCatalog
            billItems={bill?.item_list}
            noteItems={noteItems}
            noteType={noteType}
            isPhysical={isPhysical}
            isAnnulment={isAnnulment}
            reason={reason}
            itemRemainingQty={itemRemainingQty}
            isItemsCatalogCollapsed={isItemsCatalogCollapsed}
            onToggleCollapse={() => setIsItemsCatalogCollapsed(!isItemsCatalogCollapsed)}
            onAddItem={handleAddItem}
            onAddCustomCharge={handleAddCustomCharge}
          />

          <NoteItemsDetailCard
            noteItems={noteItems}
            billItemList={bill?.item_list}
            noteType={noteType}
            isPhysical={isPhysical}
            isAnnulment={isAnnulment}
            itemRemainingQty={itemRemainingQty}
            onUpdateItemName={updateItemName}
            onUpdateItemQuantity={updateItemQuantity}
            onUpdateItemUnitPrice={updateItemUnitPrice}
            onRemoveItem={removeItem}
          >
            <NoteSummaryFooter
              financialImpactMessage={financialImpactMessage}
              totals={totals}
              submitting={submitting}
              disabled={noteItems.length === 0}
              isMobile={config.isMobile}
            />
          </NoteItemsDetailCard>
        </Row>
      </Form>
    </div>
  )
}

export default ViewCreateNote
